import { useCallback, useRef, useState } from "react";
import { CustomModal } from "../../CustomModal";
import { useTranslation } from "react-i18next";
import ProductsSoldReportHeader from "./ProductsSoldReportHeader";
import ProductsSoldReportTable from "./ProductsSoldReportTable";
import { utils, writeFile } from "xlsx";


export interface ElementSales {
    id: number;
    names: string[];
    drops: number[];
    isExtra: boolean;
}

export interface ProductsReportResponse {
    source: string;
    startDate: Date;
    endDate: Date;
    products: {id: number, name: string}[];
    values: ElementSales[];
    runtime: number;
}

export default function ProductsSoldReportModal({
    data,
    onClose
}: {
    data: ProductsReportResponse,
    onClose: () => void;
}){
    const [open, setOpen] = useState(true);
    const { t } = useTranslation();

    const onCloseHandler = useCallback(() => {
        setOpen(false);
        setTimeout(onClose, 500);
    }, [onClose]);

    const getFileRows = useCallback(() => {
        return [
            [ t('name'), ...data.products.map(p => p.name), t('total')],
            ...data.values.map(el => [
                el.names[0],
                ...data.products.map((_, j) => el.drops[j]),
                el.drops.reduce((prev, crt) => prev + crt, 0)
            ])
        ];
    }, [data]);

    const getFileName = useCallback(() => {
        const sd = new Date(data.startDate);
        const ed = new Date(data.endDate);

        const sourceName = data.source.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_\-\.]/g, ''),
            startDate = `${sd.getFullYear()}_${(sd.getMonth()+1).toString().padStart(2, '0')}_${(sd.getDate()).toString().padStart(2, '0')}`,
            endDate = `${ed.getFullYear()}_${(ed.getMonth()+1).toString().padStart(2, '0')}_${(ed.getDate()).toString().padStart(2, '0')}`

        return `${sourceName}_${t('products')}_${startDate}-${endDate}`;
    }, [t, data.startDate, data.endDate, data.source]);

    const exportToExcel = useCallback(() => {
        const ws = utils.aoa_to_sheet(getFileRows());
        const wb = utils.book_new();

        utils.book_append_sheet(wb, ws, "Sheet1");
        writeFile(wb, `${getFileName()}.xlsx`);
    }, [getFileRows, getFileName]);

    const exportToCSV = useCallback(() => {
        const ws = utils.aoa_to_sheet(getFileRows());
        const wb = utils.book_new();

        utils.book_append_sheet(wb, ws, "Sheet1");
        const csv = utils.sheet_to_csv(ws);
        const blob = new Blob([csv], { type: 'text/plain' });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `${getFileName()}.csv`;
        link.click();
        URL.revokeObjectURL(link.href);
    }, [getFileRows, getFileName]);

    return (
        <CustomModal
            open={open}
            onClose={onCloseHandler}
            title={t('products')}
            header={(
                <ProductsSoldReportHeader
                    onExportExcel={exportToExcel}
                    onExportCSV={exportToCSV}
                />
            )}
            dialogStyle={{width: '100vw', maxWidth: 'none', paddingLeft: 10, paddingRight: 10}}
            bodyStyle={{height: '85vh'}}
        >
            <ProductsSoldReportTable
                data={data}
            />
        </CustomModal>
    )
}